import { Clock, FileCheck, KeyRound, Network, Rocket, Server, ShieldAlert, Users } from 'lucide-react';

/*
 * Content for the MSSP scenario walkthrough — Stonewatch Managed Security.
 *
 * PROVENANCE, and it differs from the other packs: there is no `mssp` demo pack
 * yet. Halcyon, Kestrel, Nordvik and Godwit take their artifact strings verbatim
 * from authored pack YAML. These are INVENTED, designed against the seed
 * specification at docs/industry-packs/mssp-ORG-seed.md so that the pack, once
 * authored, matches this page rather than the other way round.
 *
 * The seed spec also flags an open modelling question this pack cannot dodge:
 * whether business.Entity supports a client-tenant relationship distinct from
 * `is_vendor`. Resolve that before authoring.
 *
 * When the pack lands, re-derive this file from the YAML and delete this note.
 */

export const stonewatchScenario = {
  org: {
    name: 'Stonewatch Managed Security',
    subtitle: 'Five situations a managed security provider actually faces, across every tenant at once.',
    framingNote: 'a composite organization in the BOSSS demo environment',
    profile: [
      { icon: Users, text: '~340 employees, ~140 SOC analysts' },
      { icon: Network, text: '~180 client tenants' },
      { icon: Server, text: '~46,000 managed endpoints' },
      { icon: Clock, text: 'Two SOCs, follow-the-sun' },
    ],
  },
  scenarios: [
    {
      id: 'multitenant-incident',
      number: 1,
      title: 'Multi-Tenant Incident',
      tagline: 'Ransomware at one tenant, and the question of who else was reached',
      icon: ShieldAlert,
      frameworks: ['SOC 2', 'HIPAA (inherited)', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Ransomware detonates at a healthcare tenant. Containment is the easy part. The hard question '
          + 'lands within the hour: did anything reach another tenant through the shared platform? Answering '
          + '"no" is worth nothing unless it can be evidenced — across ~180 tenants, on the same day, to '
          + 'people who are entitled to ask.',
        cast: [
          { name: 'Marcus Ilunga', role: 'Director, SOC Operations' },
          { name: 'Alonzo Ferretti', role: 'Tier 3 Analyst / Incident Lead' },
          { name: 'Naomi Farrukhzad', role: 'Chief Information Security Officer' },
          { name: 'Yasmin El-Tayeb', role: 'SOC Manager, Eastern' },
        ],
      },
      timing: [
        { at: '−12 days', label: 'Ransomware detected at the tenant' },
        { at: '−12 days', label: 'Containment, same shift' },
        { at: '−11 days', label: 'Cross-tenant blast-radius assessment' },
        { at: '−9 days', label: 'Advisory issued to all tenants' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Ransomware is detected in the tenant environment and contained',
          artifact: 'Ransomware detonation — Westmere Regional Health tenant',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Blast radius is assessed across every tenant on the shared platform',
          artifact: 'Multi-Tenant Blast Radius Assessment',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'The tenant SLA clock runs against the contracted response commitment',
          artifact: 'Tenant incident — Westmere Regional Health, platinum SLA',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record, scoped to the tenant and its inherited obligations',
          artifact: 'Ransomware Incident — Healthcare Tenant, HIPAA Obligations Inherited',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Tenant isolation is verified rather than assumed, and the gap is recorded',
          artifact: 'Tenant Isolation Verification Not Evidenced at Time of Incident',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'A proactive advisory goes to every tenant with the evidence behind it',
          artifact: 'All-Tenant Security Advisory — Cross-Tenant Impact Assessment',
        },
      ],
      enables: [
        'The question every tenant asks — "were we affected" — is answered from one platform with evidence, not from 180 separate investigations.',
        'A tenant inherits its own regulatory obligations onto the incident record, so a healthcare tenant incident carries HIPAA scope automatically rather than by memory.',
      ],
      impact: {
        entities: [
          { name: 'Stonewatch SOC Operations', detail: 'Delivery arm', location: 'Harlow Bend, CO' },
          { name: 'Westmere Regional Health', detail: 'Tenant — HIPAA obligations', location: 'Boise, ID' },
        ],
        locations: [
          { name: 'Primary Security Operations Center', detail: 'Office · 24/7 SOC', location: 'Harlow Bend, CO' },
          { name: 'Eastern SOC — Follow-the-Sun', detail: 'Office · overnight coverage', location: 'Raleigh, NC' },
          { name: 'Stonewatch Platform Data Center', detail: 'Data center · multi-tenant platform', location: 'Denver, CO' },
        ],
        applications: [
          { name: 'Sedgemoor multi-tenant SIEM', detail: 'Tenant isolation boundary', location: 'Denver, CO' },
          { name: 'Barrowfield multi-tenant EDR', detail: '~46,000 endpoints', location: 'Denver, CO' },
          { name: 'Ravensgate SOAR', detail: 'Cross-tenant playbooks', location: 'Denver, CO' },
        ],
        vendors: [
          { name: 'Sedgemoor Security Analytics', detail: 'Multi-tenant SIEM', location: 'Reston, VA' },
          { name: 'Barrowfield Endpoint', detail: 'Multi-tenant EDR', location: 'Austin, TX' },
          { name: 'Ravensgate Automation', detail: 'SOAR orchestration', location: 'Seattle, WA' },
        ],
        data: [
          { name: 'Client Security Telemetry', detail: '~85,000 alerts/day ingested', location: 'Denver, CO' },
          { name: 'Client PHI (under management)', detail: 'Tenant obligation — inherited', location: 'Boise, ID' },
          { name: 'Client Tenant Configuration', detail: '~180 active tenants', location: 'Denver, CO' },
        ],
      },
    },
    {
      id: 'soc2-audit',
      number: 2,
      title: 'SOC 2 Type II Examination',
      tagline: 'The report every tenant relies on, and the exception in it',
      icon: FileCheck,
      frameworks: ['SOC 2 Type II', 'ISO 27001', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Stonewatch is a service organisation, which inverts the usual posture: here the audited party '
          + 'is the security vendor, and the readers are the tenants who rely on the report. A twelve-month '
          + 'observation window, control evidence gathered continuously, and one change-management exception '
          + 'that has to be disclosed rather than quietly closed.',
        cast: [
          { name: 'Fenella Wright', role: 'Compliance Analyst, SOC 2 owner' },
          { name: 'Sebastián Molinaro', role: 'Director, GRC' },
          { name: 'Helena Vasquez-Ruiz', role: 'General Counsel & Privacy Officer' },
        ],
      },
      timing: [
        { at: '−365 days', label: 'Observation window opens' },
        { at: '−30 days', label: 'Window closes' },
        { at: '−25 days', label: 'Auditor fieldwork' },
        { at: '−5 days', label: 'Report issued to tenants' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Programme activation tracks every trust-services criterion to a control',
          artifact: 'SOC 2 Type II Programme',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A formal assessment runs against the criteria across the observation window',
          artifact: 'SOC 2 Type II — Annual Examination (2026)',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Control operation is evidenced continuously rather than reconstructed at year end',
          artifact: 'Continuous control operation evidence',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The change-management exception is recorded for disclosure',
          artifact: 'SOC 2 CC8.1 — Change Management Exception in Observation Window',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The report tenants rely on is issued from the evidence chain behind it',
          artifact: 'SOC 2 Type II Report',
        },
      ],
      enables: [
        'Evidence accumulates across the observation window rather than being assembled in the fortnight before fieldwork, which is the difference between a Type II and a Type I in practice.',
        'An exception is a disclosed record with a remediation owner, which is what a tenant reading the report actually wants to see.',
      ],
      impact: {
        entities: [
          { name: 'Stonewatch Managed Security', detail: 'Whole-organisation scope', location: 'Harlow Bend, CO' },
          { name: 'Stonewatch Federal', detail: 'Public-sector practice' },
        ],
        locations: [
          { name: 'Stonewatch Headquarters', detail: 'Headquarters', location: 'Harlow Bend, CO' },
          { name: 'Primary Security Operations Center', detail: 'Office · in scope', location: 'Harlow Bend, CO' },
          { name: 'Stonewatch Platform Data Center', detail: 'Data center · in scope', location: 'Denver, CO' },
        ],
        applications: [
          { name: 'Sedgemoor multi-tenant SIEM', detail: 'In examination scope', location: 'Denver, CO' },
          { name: 'Ferrous Service Management', detail: 'SLA evidence source', location: 'Denver, CO' },
          { name: 'Portside Identity', detail: 'Workforce access evidence', location: 'Harlow Bend, CO' },
        ],
        vendors: [
          { name: 'Ashvale Assurance Group', detail: 'SOC 2 and ISO 27001 auditor', location: 'Chicago, IL' },
          { name: 'Brackwater Cloud Infrastructure', detail: 'Platform hosting — subservice organisation', location: 'Kansas City, MO' },
        ],
        data: [
          { name: 'Client Tenant Configuration', detail: '~180 active tenants', location: 'Denver, CO' },
          { name: 'Client Contract & SLA Terms', detail: '~180 active contracts', location: 'Harlow Bend, CO' },
          { name: 'Analyst Privileged Credential', detail: '~140 analyst accounts', location: 'Harlow Bend, CO' },
        ],
      },
    },
    {
      id: 'tenant-onboarding',
      number: 3,
      title: 'Tenant Onboarding at Scale',
      tagline: 'An unknown estate, discovered and baselined into coverage',
      icon: Rocket,
      frameworks: ['CMMC 2.0 (inherited)', 'NIST CSF 2.0'],
      situation: {
        narrative: 'A manufacturing tenant signs. What Stonewatch inherits is an estate nobody has inventoried — '
          + 'the asset register and reality do not match, which is normal and is exactly what onboarding exists '
          + 'to resolve. Discovery, baseline assessment, detection coverage, first report. Time to value is the '
          + 'commercial promise, and it is measurable.',
        cast: [
          { name: 'Hector Villanueva', role: 'Onboarding Manager' },
          { name: 'Talia Bergmann', role: 'Integration Engineer' },
          { name: 'Miriam Delacroix-Yao', role: 'VP Client Success' },
          { name: 'Idowu Ashanti', role: 'Client Compliance Liaison' },
        ],
      },
      timing: [
        { at: '−45 days', label: 'Tenant contract signed' },
        { at: '−40 days', label: 'Asset discovery across the estate' },
        { at: '−14 days', label: 'Baseline and detection coverage complete' },
      ],
      response: [
        {
          module: 'CMDB',
          href: '/platform/cmdb',
          action: 'Discovery establishes what the tenant actually runs, against what was declared',
          artifact: 'Tenant estate discovery — Tallow Manufacturing',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Onboarding runs as a tracked service with stages and an owner',
          artifact: 'Tenant onboarding — Tallow Manufacturing',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A baseline assessment establishes the tenant\'s starting posture',
          artifact: 'Tenant Security Baseline Assessment — Tallow Manufacturing',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Coverage gaps found at baseline are findings the tenant can act on',
          artifact: 'Detection Coverage Gap at Onboarding Baseline',
        },
        {
          module: 'Business',
          href: '/platform/business',
          action: 'The contract records scope, SLA tier and the obligations inherited from the tenant',
          artifact: 'Tallow Manufacturing master services agreement',
        },
      ],
      enables: [
        'A new tenant becomes a scoped entity with its own inventory, baseline and inherited frameworks — not a folder of spreadsheets belonging to an account manager.',
        'The gap between a declared estate and a discovered one is a finding the tenant owns, which is a better start to a relationship than discovering it during an incident.',
      ],
      impact: {
        entities: [
          { name: 'Tallow Manufacturing', detail: 'New tenant — CMMC L2 inherited', location: 'Toledo, OH' },
          { name: 'Stonewatch SOC Operations', detail: 'Onboarding delivery', location: 'Harlow Bend, CO' },
        ],
        locations: [
          { name: 'Stonewatch Headquarters', detail: 'Headquarters · client success', location: 'Harlow Bend, CO' },
          { name: 'Primary Security Operations Center', detail: 'Office · detection engineering', location: 'Harlow Bend, CO' },
        ],
        applications: [
          { name: 'Barrowfield multi-tenant EDR', detail: 'Agent deployment', location: 'Denver, CO' },
          { name: 'Sedgemoor multi-tenant SIEM', detail: 'Telemetry onboarding', location: 'Denver, CO' },
          { name: 'Thistledown vulnerability management', detail: 'Baseline scanning', location: 'Denver, CO' },
        ],
        vendors: [
          { name: 'Thistledown Vulnerability Management', detail: 'Tenant scanning', location: 'Portland, OR' },
          { name: 'Ferrous Service Management', detail: 'Ticketing and SLA tracking', location: 'Denver, CO' },
        ],
        data: [
          { name: 'Client Endpoint Inventory', detail: '~46,000 managed endpoints', location: 'Denver, CO' },
          { name: 'Client CUI (under management)', detail: 'Tenant obligation — inherited', location: 'Toledo, OH' },
          { name: 'Client Tenant Configuration', detail: '~180 active tenants', location: 'Denver, CO' },
        ],
      },
    },
    {
      id: 'rmm-supply-chain',
      number: 4,
      title: 'Remote-Management Platform Compromise',
      tagline: 'The tool that reaches every tenant is the tool that was breached',
      icon: Network,
      frameworks: ['SOC 2', 'NIST CSF 2.0', 'ISO 27001'],
      situation: {
        narrative: 'The remote-management vendor discloses a compromise of the platform Stonewatch uses to reach '
          + 'every tenant it serves. This is the failure mode unique to managed services: one vendor whose '
          + 'breach touches every tenant simultaneously. Emergency response means credential rotation across '
          + 'all tenants, verification that none were reached, and notification regardless of the answer.',
        cast: [
          { name: 'Tobias Renner', role: 'Chief Technology Officer' },
          { name: 'Ravi Chandrasekhar', role: 'Director, Platform Engineering' },
          { name: 'Eero Lindqvist', role: 'RMM & Automation Engineer' },
          { name: 'Bjorn Halvorsen', role: 'Threat Hunter' },
        ],
      },
      timing: [
        { at: '−20 days', label: 'Vendor advisory received' },
        { at: '−20 days', label: 'Emergency credential rotation begins' },
        { at: '−16 days', label: 'All-tenant verification complete' },
        { at: '+30 days', label: 'Just-in-time access hardening' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Vendor indicators are hunted across every tenant environment at once',
          artifact: 'Remote-management vendor IOC match across tenant estate',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Worked as a supply-chain investigation spanning all tenants',
          artifact: 'Remote-Management Platform Supply-Chain Compromise',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record covering the full tenant estate',
          artifact: 'Supply-Chain Compromise — Remote Management Platform',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Standing access into tenants is recorded as the architectural weakness it is',
          artifact: 'Standing Privileged Access into Tenant Environments',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Credential rotation runs as an emergency change across every tenant',
          artifact: 'Emergency credential rotation — all tenants',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Tenant notification goes out with the verification evidence attached',
          artifact: 'Tenant Notification — Remote Management Platform Compromise',
        },
      ],
      enables: [
        'Hunting a vendor indicator across every tenant is one query against one platform, which is the only version of this response that finishes in hours rather than weeks.',
        'Standing access is a recorded finding with a remediation path to just-in-time elevation, rather than an architectural habit nobody wrote down.',
      ],
      impact: {
        entities: [
          { name: 'Stonewatch Managed Security', detail: 'All tenants in scope', location: 'Harlow Bend, CO' },
          { name: 'Tessellate Remote Management', detail: 'Vendor — compromised', location: 'Tampa, FL' },
        ],
        locations: [
          { name: 'Stonewatch Platform Data Center', detail: 'Data center · platform hosting', location: 'Denver, CO' },
          { name: 'Primary Security Operations Center', detail: 'Office · emergency response', location: 'Harlow Bend, CO' },
          { name: 'Tessellate Headquarters', detail: 'Vendor site — compromised', location: 'Tampa, FL' },
        ],
        applications: [
          { name: 'Tessellate remote-management platform', detail: 'Compromised — reaches all tenants', location: 'Denver, CO' },
          { name: 'Ferngate privileged access management', detail: 'Standing access under review', location: 'Denver, CO' },
          { name: 'Barrowfield multi-tenant EDR', detail: 'Hunt surface', location: 'Denver, CO' },
        ],
        vendors: [
          { name: 'Tessellate Remote Management', detail: 'Compromised — RMM platform', location: 'Tampa, FL' },
          { name: 'Ferngate Privileged Access', detail: 'PAM for tenant access', location: 'Boston, MA' },
        ],
        data: [
          { name: 'Analyst Privileged Credential', detail: '~140 analyst accounts rotated', location: 'Denver, CO' },
          { name: 'Client Tenant Configuration', detail: '~180 active tenants', location: 'Denver, CO' },
          { name: 'Client Endpoint Inventory', detail: '~46,000 managed endpoints', location: 'Denver, CO' },
        ],
      },
    },
    {
      id: 'analyst-access',
      number: 5,
      title: 'Analyst Access Governance',
      tagline: 'Who can reach which tenant, at what privilege, and for how long',
      icon: KeyRound,
      frameworks: ['SOC 2', 'ISO 27001', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Around 140 analysts hold access into tenant environments. The quarterly recertification asks '
          + 'three questions: who can reach which tenant, at what privilege, and whether just-in-time elevation '
          + 'is genuinely being used rather than standing access nobody revoked. Separation of duties between '
          + 'analysts who monitor and engineers who change is the control that makes the rest defensible.',
        cast: [
          { name: 'Constance Abara', role: 'Multi-Tenant Platform Engineer' },
          { name: 'Marcus Ilunga', role: 'Director, SOC Operations' },
          { name: 'Naomi Farrukhzad', role: 'Chief Information Security Officer' },
        ],
      },
      timing: [
        { at: '−15 days', label: 'Quarterly campaign opened' },
        { at: 'In flight', label: 'Standing-access exceptions under review' },
        { at: '+30 days', label: 'Campaign deadline' },
      ],
      response: [
        {
          module: 'IAM',
          href: '/platform/iam',
          action: 'Every analyst\'s access into every tenant is recertified on a quarterly cycle',
          artifact: 'Analyst Tenant Access Recertification Campaign',
        },
        {
          module: 'IAM',
          href: '/platform/iam',
          action: 'Monitoring and change privileges are held apart by an enforced matrix',
          artifact: 'Analyst and Engineer Separation of Duties Matrix',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Standing access that should be just-in-time is recorded as a finding',
          artifact: 'Standing Tenant Access Where Just-in-Time Elevation Is Required',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The governing policy defines what tenant access is permitted and on what basis',
          artifact: 'Client Tenant Access Policy',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Just-in-time elevation rollout is the remediation, with an owner and a date',
          artifact: 'Just-in-Time Elevation Rollout',
        },
      ],
      enables: [
        'Access into a tenant is governed by the same machinery as access to anything else, so a tenant can be told precisely who at Stonewatch could reach their environment and when.',
        'Separation of duties between monitoring and change is enforced by a matrix rather than by org chart convention, which is what an auditor can actually test.',
      ],
      impact: {
        entities: [
          { name: 'Stonewatch SOC Operations', detail: 'Analyst workforce', location: 'Harlow Bend, CO' },
          { name: 'Stonewatch Managed Security', detail: 'All tenants in scope', location: 'Harlow Bend, CO' },
        ],
        locations: [
          { name: 'Primary Security Operations Center', detail: 'Office · analyst workforce', location: 'Harlow Bend, CO' },
          { name: 'Eastern SOC — Follow-the-Sun', detail: 'Office · overnight analysts', location: 'Raleigh, NC' },
          { name: 'Stonewatch Federal Facility', detail: 'Office · cleared personnel', location: 'Reston, VA' },
        ],
        applications: [
          { name: 'Ferngate privileged access management', detail: 'Just-in-time elevation', location: 'Denver, CO' },
          { name: 'Portside Identity', detail: 'Workforce identity', location: 'Harlow Bend, CO' },
          { name: 'Sedgemoor multi-tenant SIEM', detail: 'Tenant access surface', location: 'Denver, CO' },
        ],
        vendors: [
          { name: 'Ferngate Privileged Access', detail: 'PAM for tenant access', location: 'Boston, MA' },
          { name: 'Portside Identity', detail: 'Workforce SSO / IdP', location: 'Seattle, WA' },
          { name: 'Ashvale Assurance Group', detail: 'Independent assessor', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'Analyst Privileged Credential', detail: '~140 analyst accounts', location: 'Harlow Bend, CO' },
          { name: 'Client Tenant Configuration', detail: '~180 active tenants', location: 'Denver, CO' },
          { name: 'Client Incident Record', detail: '~2,400 incidents/year', location: 'Denver, CO' },
        ],
      },
    },
  ],
};
